import { Stack } from '@mui/material';
import { isCPFOrCNPJ } from 'brazilian-values';
import { useFormik, FormikProvider } from 'formik';
import parsePhoneNumberFromString, {
  parsePhoneNumber,
} from 'libphonenumber-js';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';
import * as yup from 'yup';

import { PhoneInputFormik, TaxIDInputFormik, TextField } from '@woovi/form';
import { useLocalStorage } from '@woovi/hooks';
import { useMutationCallbacks } from '@woovi/relay';
import { ActionButton } from '@woovi/ui';
import {
  CUSTOMER_REGISTER_FIELD_ENUM,
  getSanitizedObject,
  registerYupValidations,
} from '@woovi/utils';

import type { PaymentMethodSelectedCustomerRegisterFormFields_charge$key } from './__generated__/PaymentMethodSelectedCustomerRegisterFormFields_charge.graphql.ts';
import type { ChargeCustomerRegisterMutation } from '../customerRegister/mutations/__generated__/ChargeCustomerRegisterMutation.graphql.ts';
import { ChargeCustomerRegister } from '../customerRegister/mutations/ChargeCustomerRegisterMutation.ts';

registerYupValidations();

type PaymentMethodSelectedCustomerRegisterFormFieldsProps = {
  charge: PaymentMethodSelectedCustomerRegisterFormFields_charge$key;
};

type PaymentMethodSelectedCustomerRegisterFormFieldsValues = {
  taxID: string;
  name: string;
  email?: string;
  phone?: string;
  paymentMethodSelected: string;
};

const PaymentMethodSelectedCustomerRegisterFormFields = (
  props: PaymentMethodSelectedCustomerRegisterFormFieldsProps,
): ReactNode => {
  const { t } = useTranslation();
  const [customerData, setCustomerData] = useLocalStorage(
    '@woovi/customer-data',
    {},
  );

  const charge =
    useFragment<PaymentMethodSelectedCustomerRegisterFormFields_charge$key>(
      graphql`
        fragment PaymentMethodSelectedCustomerRegisterFormFields_charge on Charge {
          id
          paymentMethodSelected
          customerRegisterFields
        }
      `,
      props.charge,
    );

  const [chargeCustomerRegister, isPending] =
    useMutationCallbacks<ChargeCustomerRegisterMutation>({
      name: 'ChargeCustomerRegister',
      mutation: ChargeCustomerRegister,
    });

  const initialValues = useMemo(() => {
    const sanitizedValues = getSanitizedObject({
      taxID: customerData.taxID || '',
      name: customerData.name || '',
      paymentMethodSelected: charge.paymentMethodSelected || '',
      email: charge.customerRegisterFields?.includes(
        CUSTOMER_REGISTER_FIELD_ENUM.EMAIL,
      )
        ? customerData.email || ''
        : undefined,
      phone: charge.customerRegisterFields?.includes(
        CUSTOMER_REGISTER_FIELD_ENUM.PHONE,
      )
        ? customerData.phone || ''
        : undefined,
    });

    return sanitizedValues;
  }, []);

  const formikbag =
    useFormik<PaymentMethodSelectedCustomerRegisterFormFieldsValues>({
      initialValues,
      validationSchema: yup.object().shape({
        taxID: yup
          .string()
          .test('isCPF', t('Customer TaxID is invalid'), (value) => {
            if (!value) {
              return true;
            }

            return isCPFOrCNPJ(value);
          })
          .required(t('TaxID is required')),
        name: yup.string().required(t('Name is required')),
        email: yup.string().when([], {
          is: () =>
            charge.customerRegisterFields?.includes(
              CUSTOMER_REGISTER_FIELD_ENUM.EMAIL,
            ),
          then: yup
            .string()
            .email(t('Email must be a valid email'))
            .required(t('Email is required')),
          otherwise: yup.string().notRequired(),
        }),
        phone: yup.string().when([], {
          is: () =>
            charge.customerRegisterFields?.includes(
              CUSTOMER_REGISTER_FIELD_ENUM.PHONE,
            ),
          then: yup
            .string()
            .required(t('Phone is required'))
            .test(
              'phone',
              t('Phone is invalid'),
              (value) => !!(value && parsePhoneNumber(value, 'BR')?.isValid()),
            ),
          otherwise: yup.string().notRequired(),
        }),
      }),
      validateOnMount: true,
      onSubmit: (values) => {
        const { name, email, phone, taxID } = values;

        const getPhone = (): { phone?: string } => {
          if (!phone) {
            return {};
          }

          const phoneWithoutPrefix =
            parsePhoneNumberFromString(phone)?.formatNational();

          if (!phoneWithoutPrefix) {
            return {};
          }

          return { phone: parsePhoneNumber(phone, 'BR').number };
        };

        const getEmail = (): { email?: string } => {
          if (!email) {
            return {};
          }

          return { email };
        };

        const input = {
          chargeId: charge.id,
          taxID,
          name,
          ...getEmail(),
          ...getPhone(),
        };

        setCustomerData({
          ...customerData,
          taxID,
          name,
          ...getEmail(),
          ...getPhone(),
        });

        chargeCustomerRegister({
          variables: {
            input,
          },
        });
      },
    });

  const { isValid, isSubmitting, handleSubmit } = formikbag;

  const disabled = !isValid || isSubmitting || isPending;
  const loading = isSubmitting || isPending;

  const getEmailField = (): ReactNode => {
    if (
      !charge?.customerRegisterFields?.includes(
        CUSTOMER_REGISTER_FIELD_ENUM.EMAIL,
      )
    ) {
      return null;
    }

    return (
      <TextField
        name='email'
        label={t('Email')}
        placeholder={t('Email')}
        sx={(theme) => ({
          m: 0,
          '& input[type="tel"]': {
            border: 'none',
            outline: 'none',
            padding: '16.5px 14px',
          },
          '& input[type="text"]': {
            border: 'none',
            outline: 'none',
            padding: '16.5px 14px',
          },
        })}
      />
    );
  };

  const getPhoneField = (): ReactNode => {
    if (
      !charge?.customerRegisterFields?.includes(
        CUSTOMER_REGISTER_FIELD_ENUM.PHONE,
      )
    ) {
      return null;
    }

    return (
      <PhoneInputFormik
        name='phone'
        label={t('Phone')}
        type='tel'
        defaultCountry='br'
        onlyCountries={['br']}
        sx={(theme) => ({
          m: 0,
          '& input[type="tel"]': {
            border: 'none',
            outline: 'none',
            padding: '16.5px 14px',
          },
          '& input[type="text"]': {
            border: 'none',
            outline: 'none',
            padding: '16.5px 14px',
          },
        })}
      />
    );
  };

  return (
    <FormikProvider value={formikbag}>
      <Stack sx={{ gap: 1.5 }}>
        <Stack sx={{ gap: 2 }}>
          <TaxIDInputFormik
            name='taxID'
            label={t('CPF or CNPJ')}
            placeholder={t('CPF or CNPJ')}
            sx={(theme) => ({
              m: 0,
              '& input[type="tel"]': {
                border: 'none',
                outline: 'none',
                padding: '16.5px 14px',
              },
              '& input[type="text"]': {
                border: 'none',
                outline: 'none',
                padding: '16.5px 14px',
              },
            })}
          />
          <TextField
            name='name'
            label={t('Name')}
            placeholder={t('Name')}
            sx={(theme) => ({
              m: 0,
              '& input[type="tel"]': {
                border: 'none',
                outline: 'none',
                padding: '16.5px 14px',
              },
              '& input[type="text"]': {
                border: 'none',
                outline: 'none',
                padding: '16.5px 14px',
              },
            })}
          />
          {getEmailField()}
          {getPhoneField()}
        </Stack>
        <ActionButton
          type='submit'
          disabled={disabled}
          loading={loading}
          onClick={handleSubmit}
          sx={(theme) => ({
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
            },
            '&:focus': {
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
            },
          })}
        >
          {t('Continue')}
        </ActionButton>
      </Stack>
    </FormikProvider>
  );
};

export default PaymentMethodSelectedCustomerRegisterFormFields;
